import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  User, FolderOpen, Download, TrendingUp, DollarSign, Zap, Flame, 
  Target, Clock, Award, Calendar, RefreshCw, X, BarChart2, Activity,
  FileText, ArrowUpRight, Briefcase
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend, AreaChart, Area, LineChart, Line
} from 'recharts';
import './UserDashboard.css';
import logger from '../lib/logger';

// Use relative URL in production, env variable in development/preview
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const UserDashboard = ({ onClose }) => {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/my-dashboard`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setData(await response.json());
      }
    } catch (error) {
      logger.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (num) => {
    if (num === undefined || num === null) return '0 €';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M €`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k €`;
    return `${num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const COLORS = ['#3B82F6', '#F97316', '#22C55E', '#8B5CF6', '#EC4899'];

  if (loading) {
    return (
      <div className="user-dashboard-overlay">
        <div className="user-dashboard">
          <div className="dashboard-loading">
            <RefreshCw className="spinning" size={32} />
            <p>Chargement de vos données...</p>
          </div>
        </div>
      </div>
    );
  }

  const energyData = [
    { name: 'Électricité', value: data?.kpis?.projects_electricity || 0, color: '#3B82F6' },
    { name: 'Gaz', value: data?.kpis?.projects_gas || 0, color: '#F97316' }
  ].filter(d => d.value > 0);

  return (
    <div className="user-dashboard-overlay" onClick={onClose}>
      <div className="user-dashboard" onClick={(e) => e.stopPropagation()} data-testid="user-dashboard">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-left">
            <div className="avatar">
              <User size={24} />
            </div>
            <div className="header-info">
              <h1>Mon Tableau de Bord</h1>
              <p>Bienvenue, {user?.name}</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn-refresh" onClick={fetchData} title="Actualiser">
              <RefreshCw size={18} />
            </button>
            <button className="btn-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          {/* KPI Cards */}
          <div className="kpi-section">
            <div className="kpi-card primary">
              <div className="kpi-icon"><DollarSign size={22} /></div>
              <div className="kpi-info">
                <span className="kpi-value">{formatCurrency(data?.kpis?.total_ca_pipe)}</span>
                <span className="kpi-label">Mon CA Pipe</span>
              </div>
            </div>
            <div className="kpi-card success">
              <div className="kpi-icon"><TrendingUp size={22} /></div>
              <div className="kpi-info">
                <span className="kpi-value">{formatCurrency(data?.kpis?.total_economies)}</span>
                <span className="kpi-label">Économies Clients</span>
              </div>
            </div>
            <div className="kpi-card info">
              <div className="kpi-icon"><FolderOpen size={22} /></div>
              <div className="kpi-info">
                <span className="kpi-value">{data?.kpis?.total_projects || 0}</span>
                <span className="kpi-label">Mes Projets</span>
              </div>
            </div>
            <div className="kpi-card warning">
              <div className="kpi-icon"><Download size={22} /></div>
              <div className="kpi-info">
                <span className="kpi-value">{data?.kpis?.total_exports || 0}</span>
                <span className="kpi-label">Mes Exports</span>
              </div>
            </div>
            <div className="kpi-card purple">
              <div className="kpi-icon"><Target size={22} /></div>
              <div className="kpi-info">
                <span className="kpi-value">{data?.kpis?.conversion_rate || 0}%</span>
                <span className="kpi-label">Taux Conversion</span>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="charts-row">
            {/* Monthly Projects Trend */}
            <div className="chart-card">
              <h3><BarChart2 size={18} /> Mes Projets par Mois</h3>
              {data?.monthly_trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={data.monthly_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="projects" stroke="#3B82F6" fill="#93C5FD" name="Projets" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data">Pas encore de données</div>
              )}
            </div>

            {/* Activity Trend */}
            <div className="chart-card">
              <h3><Activity size={18} /> Mon Activité (4 dernières semaines)</h3>
              {data?.activity_trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={data.activity_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="connections" stroke="#22C55E" strokeWidth={2} dot={{ fill: '#22C55E' }} name="Connexions" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data">Pas encore de données</div>
              )}
            </div>

            {/* Energy Type Distribution */}
            <div className="chart-card small">
              <h3><Zap size={18} /> Répartition Énergie</h3>
              {energyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={energyData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={65}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {energyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data">Aucun projet</div>
              )}
            </div>
          </div>

          {/* Bottom Section */}
          <div className="bottom-row">
            {/* Recent Projects */}
            <div className="list-card">
              <h3><FileText size={18} /> Mes Derniers Projets</h3>
              {data?.recent_projects?.length > 0 ? (
                <div className="project-list">
                  {data.recent_projects.map((project) => (
                    <div key={project.id || `${project.name}-${project.created_at}`} className="project-item">
                      <div className="project-icon">
                        {project.energy_type === 'gas' ? <Flame size={16} className="gas" /> : <Zap size={16} className="elec" />}
                      </div>
                      <div className="project-info">
                        <span className="project-name">{project.name}</span>
                        <span className="project-client">{project.client || 'Client non renseigné'}</span>
                      </div>
                      <div className="project-meta">
                        <span className="project-value">{formatCurrency(project.prestation_value)}</span>
                        <span className="project-date">{formatDate(project.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-data">Aucun projet récent</div>
              )}
            </div>

            {/* Top Clients */}
            <div className="list-card">
              <h3><Award size={18} /> Mes Top Clients</h3>
              {data?.top_clients?.length > 0 ? (
                <div className="client-list">
                  {data.top_clients.map((client, i) => (
                    <div key={`${client.client}-${client.energy_type}`} className="client-item">
                      <span className="client-rank">{i + 1}</span>
                      <div className="client-icon">
                        {client.energy_type === 'gas' ? <Flame size={14} className="gas" /> : <Zap size={14} className="elec" />}
                      </div>
                      <span className="client-name">{client.client}</span>
                      <span className="client-value">{formatCurrency(client.prestation)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-data">Aucun client</div>
              )}
            </div>

            {/* Stats Summary */}
            <div className="stats-card">
              <h3><Briefcase size={18} /> Résumé</h3>
              <div className="stats-list">
                <div className="stat-item">
                  <span className="stat-label">Connexions (30j)</span>
                  <span className="stat-value">{data?.kpis?.connections_30d || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Durée moy. session</span>
                  <span className="stat-value">{data?.kpis?.avg_session_duration || 0} min</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Projets Électricité</span>
                  <span className="stat-value elec">{data?.kpis?.projects_electricity || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Projets Gaz</span>
                  <span className="stat-value gas">{data?.kpis?.projects_gas || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
